import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, FormControl, InputLabel, Select, MenuItem,
  Snackbar, Alert
} from '@mui/material';

interface ReminderDialogProps {
  open: boolean;
  onClose: () => void;
  onSend: (message: string, method: 'email' | 'sms' | 'both') => Promise<void>;
}

const ReminderDialog: React.FC<ReminderDialogProps> = ({ open, onClose, onSend }) => {
  const [message, setMessage] = useState('');
  const [method, setMethod] = useState<'email' | 'sms' | 'both'>('email');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleSend = async () => {
    setLoading(true);
    try {
      await onSend(message, method);
      setAlert({ type: 'success', message: 'Reminder sent successfully' });
      setMessage('');
      setMethod('email');
      onClose();
    } catch (err) {
      setAlert({ type: 'error', message: 'Failed to send reminder' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Send Payment Reminder</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Reminder Message"
            fullWidth
            multiline
            minRows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            sx={{ mb: 3 }}
          />

          <FormControl fullWidth>
            <InputLabel>Send Method</InputLabel>
            <Select
              value={method}
              label="Send Method"
              onChange={(e) => setMethod(e.target.value as 'email' | 'sms' | 'both')}
            >
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="sms">SMS</MenuItem>
              <MenuItem value="both">Email + SMS</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button onClick={handleSend} disabled={loading || !message} variant="contained">
            {loading ? 'Sending...' : 'Send Reminder'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!alert}
        autoHideDuration={4000}
        onClose={() => setAlert(null)}
      >
        <Alert
          onClose={() => setAlert(null)}
          severity={alert?.type}
          variant="filled"
        >
          {alert?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ReminderDialog;
